import { Injectable } from "@angular/core";
import { ICommonProductOrderModel, IProductModel } from "src/app/modules/shared/components/common-product-add-form/models/product.model";

@Injectable({
    providedIn: "root",
})
export class OpportunityProductToCommonProductModelBuilderService {
    transformOpportunityProductToModel(opportunityProducts: any[], exchangeRate: number): IProductModel[] {
        return opportunityProducts.map((product) => this.mapProductToModel(product, exchangeRate));
    }

    private mapProductToModel(product: any, exchangeRate: number): IProductModel {
        const taxTotal = this.getTotalTaxAmount(this.getProductSubTax(product?.opportunityProductSubTax, exchangeRate));
        const productModel: IProductModel = {
            id: product?.id ?? 0,
            createdSource: product?.createdSource ?? "",
            updatedSource: product?.updatedSource ?? null,
            createdBy: product?.createdBy ?? 0,
            updatedBy: product?.updatedBy ?? null,
            createdDate: product?.createdDate ?? "",
            updatedDate: product?.updatedDate ?? null,
            productId: product?.productId ?? 0,
            itemNo: product?.itemNo ?? "",
            description: product?.description ?? "",
            uomId: product?.uomId ?? 0,
            uom: product?.uom ?? null,
            unit: product?.uom ?? null,
            qty: product?.qty ?? 0,
            totalQuantity: product?.totalQty ?? 0,
            price: Number(product?.price ?? 0) * (exchangeRate ?? 1),
            totalAmount: Number(product?.totalAmount ?? 0) * (exchangeRate ?? 1),
            totalCostPrice: Number(product?.totalCostPrice ?? 0) * (exchangeRate ?? 1),
            costPrice: Number(product?.costPrice ?? 0) * (exchangeRate ?? 1),
            subTotal: Number(product?.subTotal ?? 0) * (exchangeRate ?? 1),
            baseSubTotal: Number(product?.subTotal ?? 0) * (exchangeRate ?? 1),
            statusId: product?.statusId ?? 1,
            warehouseId: product?.warehouseId ?? 0,
            bundleDetails: [],
            typeId: product?.productTypeId ?? 0,
            discount: { id: product?.discountId ?? 0 },
            discountRate: product?.discountRate ?? 0,
            discountAmount: Number(product?.discountAmount ?? 0) * (exchangeRate ?? 1),
            additionalDiscount: null,
            additionalDiscountRate: 0,
            additionalDiscountAmount: 0,
            handlingFee: { id: product?.additionalFeesId ?? 0 },
            handlingFeeRate: product?.additionalRate ?? 0,   
            handlingFeeAmount: Number(product?.additionalAmount ?? 0) * (exchangeRate ?? 1),
            taxAmount: Number(taxTotal ?? 0) * (exchangeRate ?? 1),
            tax: Number(taxTotal ?? 0) * (exchangeRate ?? 1),
            subTaxes: this.getProductSubTax(product?.opportunityProductSubTax ?? [], exchangeRate),
        };

        if (product?.opportunityBundleProducts && product?.opportunityBundleProducts?.length > 0) {
            productModel.bundleDetails = this.mapBundles(product?.opportunityBundleProducts, exchangeRate);
        }

        return productModel;
    }



    private mapBundles(bundleProducts: any[], exchangeRate: number): IProductModel[] {
        return bundleProducts.map((bundle) => {
            const taxTotal = this.getTotalTaxAmount(this.getProductSubTax(bundle?.opportunityBundleDetailSubTax, exchangeRate));
            return {
                id: bundle?.id ?? 0,
                createdSource: bundle?.createdSource ?? "",
                updatedSource: bundle?.updatedSource ?? null,
                createdBy: bundle?.createdBy ?? 0,
                updatedBy: bundle?.updatedBy ?? null,
                createdDate: bundle?.createdDate ?? "",
                updatedDate: bundle?.updatedDate ?? null,
                productId: bundle?.productId ?? 0,
                itemNo: bundle?.itemNo ?? "",
                description: bundle?.description ?? "",
                uomId: bundle?.uomId ?? 0,
                uom: bundle?.uom ?? null,
                unit: bundle?.uom ?? null,
                qty: bundle?.orderQty ?? 0,
                totalQuantity: bundle?.totalQty ?? 0,
                price: Number(bundle?.price ?? 0) * (exchangeRate ?? 1),
                totalAmount: Number(bundle?.totalAmount ?? 0) * (exchangeRate ?? 1),
                totalCostPrice: Number(bundle?.totalCostPrice ?? 0) * (exchangeRate ?? 1),
                costPrice: Number(bundle?.costPrice ?? 0) * (exchangeRate ?? 1),
                subTotal: Number(bundle?.subTotal ?? 0) * (exchangeRate ?? 1),
                baseSubTotal: Number(bundle?.subTotal ?? 0) * (exchangeRate ?? 1),
                statusId: bundle?.statusId ?? 1,
                warehouseId: bundle?.warehouseId ?? 0,
                bundleDetails: [],
                typeId: bundle?.productTypeId ?? 0,
                discount: { id: bundle?.discountId ?? 0 },
                discountRate: bundle?.discountRate ?? 0,
                discountAmount: Number(bundle?.discountAmount ?? 0) * (exchangeRate ?? 1),
                additionalDiscount: null,
                additionalDiscountRate: 0,
                additionalDiscountAmount: 0,
                handlingFee: { id: bundle?.additionalFeesId ?? 0 },
                handlingFeeRate: bundle?.additionalRate ?? 0,
                handlingFeeAmount: Number(bundle?.additionalAmount ?? 0) * (exchangeRate ?? 1),
                taxAmount: Number(taxTotal ?? 0) * (exchangeRate ?? 1),
                tax: Number(taxTotal) * (exchangeRate ?? 1),
                subTaxes: this.getProductSubTax(bundle?.opportunityBundleDetailSubTax ?? [], exchangeRate),
            };
        });
    }

    getProductSubTax(taxDetails: any[], exchangeRate: number): any[] {
        const subTaxes = taxDetails?.map((tax) => ({...tax, taxAmount: Number(tax.taxAmount ?? 0) * (exchangeRate ?? 1)}));
        return subTaxes;
    }

    getTotalTaxAmount(taxDetails: any[]): number {
        return taxDetails?.reduce((sum, item) => sum + (item.taxAmount ?? 0), 0);
    }

    buildCommonProductOrderModel(productOrderModel: any): ICommonProductOrderModel {
        const commonProductOrderModel: ICommonProductOrderModel = {
            products: this.transformOpportunityProductToModel(productOrderModel?.opportunityProducts, productOrderModel?.exchangeRate),
            orderSubtotal: Number(productOrderModel?.totalAmount ?? 0) * (productOrderModel?.exchangeRate ?? 1),
            orderBaseSubtotal: Number(productOrderModel?.totalAmount ?? 0) * (productOrderModel?.exchangeRate ?? 1),
            discount: { id: productOrderModel?.discountId ?? 0 },
            discountRate: productOrderModel?.discountRate ?? 0,
            discountAmount: Number(productOrderModel?.discountAmount ?? 0) * (productOrderModel?.exchangeRate ?? 1),
            taxAmount: Number(productOrderModel?.taxAmount ?? 0) * (productOrderModel?.exchangeRate ?? 1),
            handlingFee: { id: productOrderModel?.additionalFeesId ?? 0 },
            handlingFeeRate: productOrderModel?.additionalRate ?? 0,
            handlingFeeAmount: Number(productOrderModel?.additionalAmount ?? 0) * (productOrderModel?.exchangeRate ?? 1),
            additionalDiscount: productOrderModel?.opportunityAddDiscount && productOrderModel?.opportunityAddDiscount.length > 0 ? {id: productOrderModel?.opportunityAddDiscount[0]?.id ?? 0, discountId: productOrderModel?.opportunityAddDiscount[0]?.discountId} : null,
            additionalDiscountRate: productOrderModel?.opportunityAddDiscount && productOrderModel?.opportunityAddDiscount.length > 0 ? productOrderModel?.opportunityAddDiscount[0]?.discountRate ?? 0 : 0,
            additionalDiscountAmount: productOrderModel?.opportunityAddDiscount && productOrderModel?.opportunityAddDiscount.length > 0 ? Number(productOrderModel?.opportunityAddDiscount[0]?.discountAmount ?? 0) * (productOrderModel?.exchangeRate ?? 1) : 0,
            shippingCharge: Number(productOrderModel?.shippingChargeAmount ?? 0) * (productOrderModel?.exchangeRate ?? 1),
            totalAmount: Number(productOrderModel?.netAmount ?? 0) * (productOrderModel?.exchangeRate ?? 1),
        };
        return commonProductOrderModel;
    }
}
